import { useState, useEffect, useCallback } from 'react'
import apiClient from '@/lib/api-client'
import type { User, Profile, GalleryImage, Service, Availability } from '@/types'

interface DashboardProfile extends Profile {
  gallery: GalleryImage[]
  services: Service[]
  availability: Availability[]
}

interface ProfileResponse {
  data: {
    user: User
    profile: DashboardProfile | null
    profile_completeness: number
  }
}

export function useProfile() {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<DashboardProfile | null>(null)
  const [completeness, setCompleteness] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchProfile = useCallback(async () => {
    try {
      const response = await apiClient.get<ProfileResponse>('/user/profile')
      setUser(response.data.data.user)
      setProfile(response.data.data.profile)
      setCompleteness(response.data.data.profile_completeness)
      setError(null)
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  const updateProfile = useCallback(async () => {
    await fetchProfile()
  }, [fetchProfile])

  const needsOnboarding = completeness === 0 && !profile?.business_name

  return {
    user,
    profile,
    completeness,
    isLoading,
    error,
    updateProfile,
    needsOnboarding,
  }
}
